// Libs
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
// Utils
import GENERAL from 'utils/constants/general';
import { GlobalUtils } from 'utils';
import { getCoords } from 'utils/libs';
import DataFieldManagerUtils from 'components/DataFieldManager/DataFieldManagerUtils';
import { getUIValueProp } from 'components/DataFieldManager/OnlyReadField/OnlyReadField';
// Actions
import { sendToast } from 'screens/Dashboard/actions';
import { updateDatasourceItem } from 'core/actions';
// Hooks
import useOrderApi from './useOrderApi';
import {
	buildUserAssignmentOrders,
	getCommonUserAssignments,
	groupOrdersByService,
	getUserAssignmentCompatibilityMessage,
} from './useOrderAssignments.utils';

const { UNSUCCESS } = GENERAL.ENV.STATUS;
const { ENTITY_ID, ENTITY_NAME } = DataFieldManagerUtils.getFieldIds();
const MISSING_ASSIGNMENT_RESULT_MESSAGE =
	'No se recibió el resultado de la reasignación';

const normalizeRows = rowData =>
	Array.isArray(rowData) ? rowData : rowData ? [rowData] : [];

const useOrderUserAssignment = () => {
	const dispatch = useDispatch();
	const { getUserAssignment, setUserAssignment } = useOrderApi();
	const isMountedRef = useRef(true);
	const userAssignmentRequestIdRef = useRef(0);
	const lastUserAssignmentRequestRef = useRef();

	const [state, setState] = useState({
		isLoading: false,
		isSetting: false,
		data: [],
		status: undefined,
		message: undefined,
		selectedAssignedTechId: undefined,
		selectedAssignedTechEntityId: undefined,
	});
	const setMountedState = update => {
		if (isMountedRef.current) setState(update);
	};

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			userAssignmentRequestIdRef.current += 1;
		};
	}, []);

	const onGetUserAssignment = request => {
		const { orders, serviceId, zoneId, centralId, searchValue } = request;
		const requestedOrders = orders || [{ serviceId, zoneId, centralId }];
		const requestId = ++userAssignmentRequestIdRef.current;
		const compatibilityMessage =
			getUserAssignmentCompatibilityMessage(requestedOrders);
		if (compatibilityMessage) {
			setMountedState(prev => ({
				...prev,
				isLoading: false,
				data: [],
				status: undefined,
				message: undefined,
			}));
			dispatch(sendToast({ message: compatibilityMessage, type: 'warn' }));
			return;
		}
		lastUserAssignmentRequestRef.current = request;
		const serviceGroups = groupOrdersByService(requestedOrders);
		setMountedState(prev => ({
			...prev,
			isLoading: true,
			data: [],
			status: undefined,
			message: undefined,
		}));
		return Promise.all(
			serviceGroups.map(group =>
				getUserAssignment(group.orders, {
					serviceId: group.serviceId,
					searchValue,
				}),
			),
		)
			.then(groupResponses => {
				if (
					!isMountedRef.current ||
					requestId !== userAssignmentRequestIdRef.current
				)
					return;
				const hasResponseForEveryOrder = groupResponses.every(
					(response, index) =>
						Array.isArray(response) &&
						response.length === serviceGroups[index].orders.length,
				);
				const responses = hasResponseForEveryOrder
					? groupResponses.reduce(
							(allResponses, response) => allResponses.concat(response),
							[],
						)
					: [];
				setMountedState(prev => ({
					...prev,
					isLoading: false,
					data: getCommonUserAssignments(responses),
				}));
			})
			.catch(err => {
				if (
					!isMountedRef.current ||
					requestId !== userAssignmentRequestIdRef.current
				)
					return;
				setMountedState(prev => ({
					...prev,
					isLoading: false,
					data: [],
					status: UNSUCCESS,
					message: err.message || '',
				}));
				dispatch(sendToast({ message: err.message, type: err.type }));
			});
	};

	const onRetry = () => {
		if (!lastUserAssignmentRequestRef.current) return;
		return onGetUserAssignment(lastUserAssignmentRequestRef.current);
	};

	const onSetUserAssignment = async ({ field, rowData, option }, callback) => {
		const rows = normalizeRows(rowData);
		if (!rows.length) return;
		const compatibilityMessage = getUserAssignmentCompatibilityMessage(rows);
		if (compatibilityMessage) {
			dispatch(sendToast({ message: compatibilityMessage, type: 'warn' }));
			return;
		}
		setMountedState(prev => ({
			...prev,
			isSetting: true,
			status: undefined,
			message: undefined,
		}));

		const getUpdateRecords = (data, { field, option }) => {
			const record = {
				fields: {
					[field.id]: option.id,
					[getUIValueProp(field) || field.name]: option.name,
					[ENTITY_ID]: option.entityId,
					[ENTITY_NAME]: option.entityName,
				},
			};
			const isArray = Array.isArray(data);

			return isArray
				? data.map(({ id }) => ({ ...record, id }))
				: [
						{
							...record,
							id: data.id,
						},
					];
		};

		getCoords()
			.then(({ coords }) => {
				setTimeout(() => {
					Promise.all(
						groupOrdersByService(rows).map(group =>
							setUserAssignment(
								buildUserAssignmentOrders(
									group.orders,
									option.id,
									option.entityId,
								),
								{
									serviceId: group.serviceId,
									coords,
								},
							).catch(err =>
								group.orders.map(({ id }) => ({
									id,
									status: UNSUCCESS,
									message: err?.message || '',
								})),
							),
						),
					).then(groupResults => {
						const resultsByOrderId = new Map(
							groupResults
								.reduce(
									(results, groupResult) =>
										results.concat(GlobalUtils.checkArray(groupResult)),
									[],
								)
								.filter(
									result =>
										result && result.id !== undefined && result.id !== null,
								)
								.map(result => [String(result.id), result]),
						);
						const processed = rows.map(
							row =>
								resultsByOrderId.get(String(row.id)) || {
									id: row.id,
									status: UNSUCCESS,
									message: MISSING_ASSIGNMENT_RESULT_MESSAGE,
								},
						);
						const successfulOrders = processed.filter(
							order => order.status !== UNSUCCESS,
						);
						const failedOrders = processed.filter(
							order => order.status === UNSUCCESS,
						);
						setMountedState(prev => ({
							...prev,
							isSetting: false,
							selectedAssignedTechId: successfulOrders.length
								? option.id
								: prev.selectedAssignedTechId,
							selectedAssignedTechEntityId: successfulOrders.length
								? option.entityId
								: prev.selectedAssignedTechEntityId,
						}));

						if (failedOrders.length) {
							dispatch(
								sendToast({
									message:
										failedOrders.length === rows.length
											? failedOrders[0].message
											: `${failedOrders.length} de ${rows.length} órdenes no pudieron reasignarse`,
									type: 'error',
								}),
							);
						}

						if (successfulOrders.length) {
							dispatch(
								updateDatasourceItem({
									updateRecords: getUpdateRecords(successfulOrders, {
										field,
										option,
									}),
								}),
							);
						}
						if (callback) callback(processed);
					});
				}, 100);
			})
			.catch(({ message, type }) => {
				setMountedState(prev => ({ ...prev, isSetting: false }));
				dispatch(sendToast({ message, type }));
			});
	};

	return { state, onGetUserAssignment, onSetUserAssignment, onRetry };
};

export default useOrderUserAssignment;
